import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { trackAddToCart } from '@/lib/metaPixel';

export interface SavedTutor {
  id: string;
  name: string;
  profilePicture?: string;
  profilePictureUrl?: string;
  subjects: string[];
  hourlyRate: number;
  rating?: number;
  reviewCount?: number;
  sessionPreference?: 'chat_only' | 'video_only' | 'both';
  paymentMethods?: string[];
  pricingCurrency?: string;
}

export const useSavedTutors = () => {
  const [savedTutors, setSavedTutors] = useState<SavedTutor[]>([]);
  const { user } = useAuth();

  // Get current user ID
  const getUserId = () => {
    return user?.id || null;
  };

  // Get user-specific localStorage key
  const getStorageKey = (userId: string) => {
    return `savedTutors_${userId}`;
  };

  useEffect(() => {
    const userId = getUserId();
    
    if (userId) {
      // Load saved tutors for this specific user
      const storageKey = getStorageKey(userId);
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          setSavedTutors(JSON.parse(saved));
        } catch (error) {
          console.error('Error parsing saved tutors:', error);
          setSavedTutors([]);
        }
      } else {
        setSavedTutors([]);
      }
    } else {
      // Clear saved tutors when user logs out
      setSavedTutors([]);
    }
  }, [user]);

  const saveTutor = (tutor: any) => {
    const userId = getUserId();
    
    // Only allow saving if user is logged in
    if (!userId) {
      return;
    }

    const tutorToSave: SavedTutor = {
      id: tutor.id,
      name: tutor.name || tutor.expand?.user?.name || 'Unknown Tutor',
      profilePicture: tutor.profilePicture,
      profilePictureUrl: tutor.profilePictureUrl,
      subjects: tutor.subjects || [],
      hourlyRate: tutor.hourlyRate || 0,
      rating: tutor.rating,
      reviewCount: tutor.reviewCount,
      sessionPreference: tutor.sessionPreference,
      paymentMethods: tutor.paymentMethods,
      pricingCurrency: tutor.pricingCurrency
    };

    const updatedSaved = [...savedTutors, tutorToSave];
    setSavedTutors(updatedSaved);
    
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(updatedSaved));

    // Track AddToCart event for Meta Pixel when user saves a tutor
    trackAddToCart({
      id: tutor.id,
      title: tutorToSave.name,
      price: tutorToSave.hourlyRate || 0,
      category: tutorToSave.subjects?.[0] || 'Tutoring'
    }, 1);
  };

  const removeTutor = (tutorId: string) => {
    const userId = getUserId();
    
    if (!userId) {
      return;
    }

    const updatedSaved = savedTutors.filter(tutor => tutor.id !== tutorId);
    setSavedTutors(updatedSaved);
    
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(updatedSaved));
  };

  const isTutorSaved = (tutorId: string) => {
    return savedTutors.some(tutor => tutor.id === tutorId);
  };

  const toggleSavedTutor = (tutor: any) => {
    const userId = getUserId();
    
    // Only allow toggling if user is logged in
    if (!userId) {
      return;
    }

    if (isTutorSaved(tutor.id)) {
      removeTutor(tutor.id);
    } else {
      saveTutor(tutor);
    }
  };

  return {
    savedTutors,
    saveTutor,
    removeTutor,
    isTutorSaved,
    toggleSavedTutor
  };
};
